import {noop} from '@github-ui/noop'
import {SIDE_PANEL_PORTAL_ROOT, SidePanel as SidePanelComponent} from '@github-ui/side-panel'
import {testIdProps} from '@github-ui/test-id-props'
import {useKeyPress} from '@github-ui/use-key-press'
import {PageLayout, registerPortalRoot, useOverlay} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentProps, memo, useCallback, useLayoutEffect, useMemo, useRef} from 'react'

import {SidePanelTypeParam} from '../../api/memex-items/side-panel-item'
import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useReturnFocus} from '../../hooks/common/use-return-focus'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useRootOffsetY} from '../../hooks/use-root-offset-y'
import {type SidePanelState, useSidePanel} from '../../hooks/use-side-panel'
import {useSiteHeaderHeight} from '../../hooks/use-site-header-height'
import {Resources} from '../../strings'
import {SidePanelContent} from './content'
import styles from './index.module.css'

interface SidePanelProps {
  mainAppContent: React.ReactNode
}

export const SidePanel = memo(function SidePanel({mainAppContent}: SidePanelProps) {
  const {closePane, sidePanelState, isPaneOpened, supportedItemTypes, pinned, containerRef, initialFocusRef} =
    useSidePanel()

  const isOpen =
    (isPaneOpened &&
      sidePanelState &&
      (sidePanelState.type !== SidePanelTypeParam.ISSUE || supportedItemTypes.has(sidePanelState.item.contentType))) ??
    false

  const content = sidePanelState && (
    <div ref={containerRef} className={styles.sidePanelWrapper}>
      {/* It's not great to start focus at a useless unlabeled element - much better would be the side panel heading.
      But the new issue viewer shows a skeleton when loading, so there's initially nothing to focus. And we'd have to
      then shift focus to the heading once the content loads, which would be annoying and unexpected. So instead we
      focus this empty element, which at least guarantees that we have a focusable target and that screen readers will
      announce the panel label. Also note we don't want to focus the close button because users will want to press
      space to scroll down in long panel contents and that will close the panel. */}
      <span
        tabIndex={-1}
        ref={initialFocusRef}
        {...testIdProps('side-panel-focus-target')}
        data-prevent-scroll-on-focus
      />

      <SidePanelContent sidePanelState={sidePanelState} />
    </div>
  )

  const label = buildSidePanelLabel(sidePanelState)

  const onEscape = useCallback(
    (e: Pick<Event, 'target'>) => {
      const target = e.target as HTMLElement | null

      // If focus is in the site-search input, let that handle Escape
      if (target?.classList.contains('js-site-search-field')) {
        return
      }

      // If any omnibar picker is open, don’t close the panel
      if (document.querySelector('.omnibar-picker.visible')) {
        return
      }

      closePane()
    },
    [closePane],
  )

  const effectivePinned = pinned && sidePanelState?.type !== SidePanelTypeParam.AGENT_TASK

  // Always rendering (but hiding) both the modal and the docked pane ensures that animations work and performance is
  // maximized when pinning/unpinning
  // Conditionally rendering the children helps avoid unnecessary re-renders of the hidden, memoized one, and
  // unnecessary HTML in the DOM
  return (
    <>
      <DockedSidePanel
        open={isOpen && effectivePinned}
        mainAppContent={mainAppContent}
        label={label}
        initialFocusRef={initialFocusRef}
        onEscape={onEscape}
      >
        {isOpen && effectivePinned && content}
      </DockedSidePanel>
      <OverlaySidePanel
        open={isOpen && !effectivePinned}
        label={label}
        onEscape={onEscape}
        contentType={sidePanelState?.type}
        initialFocusRef={initialFocusRef}
      >
        {isOpen && !effectivePinned && content}
      </OverlaySidePanel>
    </>
  )
})

interface DockedSidePanelProps {
  mainAppContent: React.ReactNode
  children: React.ReactNode
  open: boolean
  label: string
  initialFocusRef: React.RefObject<HTMLElement | null>
  onEscape: (e: KeyboardEvent) => void
}

/**
 * Try to focus a ref, falling back to another ref if necessary. Avoids focusing unmounted elements.
 */
const tryFocus = (...refs: Array<React.RefObject<HTMLElement | null>>) => {
  for (const {current} of refs)
    if (current && document.body.contains(current)) {
      current.focus()
      return
    }
}

const DockedSidePanel = memo(function DockedSidePanel({
  mainAppContent,
  children,
  open,
  label,
  initialFocusRef,
  onEscape,
}: DockedSidePanelProps) {
  const {memex_small_viewport_a11y} = useEnabledFeatures()
  const {postStats} = usePostStats()
  const memexAppOffsetY = useRootOffsetY()

  const returnFocusOutRef = useRef<HTMLElement | null>(null)
  const returnFocusInRef = useRef<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const onFocus = useCallback((event: React.FocusEvent) => {
    const from = event.relatedTarget
    const container = event.currentTarget
    const element = event.target

    if (element instanceof HTMLElement) returnFocusInRef.current = element

    if (!container.contains(from) && from instanceof HTMLElement) {
      // focus is entering the region
      returnFocusOutRef.current = from

      // try to return focus to where it was before, if possible
      tryFocus(returnFocusInRef)
    }
  }, [])

  useReturnFocus(open, returnFocusOutRef)

  // Listen for ctrl+f6 shortcut (consistent with Slack app) to move focus to/from the side panel
  useKeyPress(
    ['F6'],
    event => {
      event.preventDefault()

      // be careful not to focus things that aren't in the document
      if (containerRef.current?.contains(document.activeElement)) {
        tryFocus(returnFocusOutRef)
        // there's no good fallback for the main content, but it also shouldn't ever be necessary
        postStats({name: 'region_focus', context: 'target: main'})
      } else {
        tryFocus(returnFocusInRef, initialFocusRef)
        postStats({name: 'region_focus', context: 'target: side panel'})
      }
    },
    {
      ctrlKey: true,
      triggerWhenInputElementHasFocus: true,
      triggerWhenPortalIsActive: false,
    },
  )

  // The docked panel is not an overlay so this is a lie, but `useOverlay` will register it onto the global overlay
  // stack that Primer maintains. This allows it to work well with other actual overlays, preventing us from closing
  // the panel if for example, a picker is open, because Primer will not call `onEscape` if there is another overlay
  // that was opened after this one. We can't just use regular DOM event bubbling (where we'd call `stopPropagation`
  // in the child overlay) because overlays are all rendered into a root portal. Once overlays all use the Popover API
  // this won't be necessary.
  const nullRef = useRef(null)
  useOverlay({overlayRef: nullRef, returnFocusRef: nullRef, onEscape, onClickOutside: noop})

  // The overlay variant of the side panel (rendered by `OverlaySidePanel` via `<SidePanel>`) registers itself as the
  // `SIDE_PANEL_PORTAL_ROOT` portal root so that the issue viewer's kebab menu (which sets `insideSidePanel: true` and
  // requests that portal name) has somewhere to render. The docked variant doesn't wrap content in `<SidePanel>`, so
  // nothing was registering the name — opening the description kebab on a pinned project work item threw
  // "Portal container 'sidePanelPortalRoot' is not yet registered". Register the docked `<aside>` as the portal root
  // when the panel is open. The registry is last-write-wins, so re-registering whenever the docked panel becomes open
  // recovers from any prior registration left behind by the overlay variant when the user toggles pinning.
  // See github/issues#22475.
  useLayoutEffect(() => {
    if (open && containerRef.current) {
      registerPortalRoot(containerRef.current, SIDE_PANEL_PORTAL_ROOT)
    }
  }, [open])

  return (
    <PageLayout
      containerWidth="full"
      padding="none"
      columnGap="none"
      rowGap="none"
      style={{
        '--memex-app-offset-y': `${memexAppOffsetY}px`,
      }}
      className={clsx(styles.sidePanelLayout, memex_small_viewport_a11y && styles.smallViewport)}
    >
      <PageLayout.Content
        className={clsx(styles.sidePanelMainContent, memex_small_viewport_a11y && styles.smallViewport)}
      >
        <div className={styles.sidePanelContentContainer}>{mainAppContent}</div>
      </PageLayout.Content>
      <PageLayout.Pane
        sticky
        resizable
        hidden={!open}
        width="large"
        widthStorageKey="projects.sidePanelWidth"
        aria-label={label}
        divider="line"
        className={styles.sidePanelPaneSettings}
      >
        <aside aria-label={label} onFocus={onFocus} tabIndex={-1} ref={containerRef} className={styles.sidePanelPane}>
          {children}
        </aside>
      </PageLayout.Pane>
    </PageLayout>
  )
})

interface OverlaySidePanelProps {
  children: React.ReactNode
  open: boolean
  label: string
  onEscape: (event: Parameters<ComponentProps<typeof SidePanelComponent>['onClose']>[0]) => void
  contentType: SidePanelTypeParam | undefined
  initialFocusRef: React.RefObject<HTMLElement | null>
}

const OverlaySidePanel = memo(function OverlaySidePanel({
  children,
  open,
  label,
  onEscape,
  contentType,
  initialFocusRef,
}: OverlaySidePanelProps) {
  const siteHeaderHeight = useSiteHeaderHeight()
  // unused but required by the Overlay component
  const returnFocusRef = useRef(null)

  /* Cache the width to avoid flashing to full-width when transitioning to unload the side panel */
  const prevSidePanelWidth = useRef<string | undefined>(undefined)
  const sidePanelWidth = useMemo(() => {
    switch (contentType) {
      case SidePanelTypeParam.ISSUE:
        return (prevSidePanelWidth.current = 'min(90%, 1280px)')
      case SidePanelTypeParam.INFO:
        return (prevSidePanelWidth.current = '580px')
      case SidePanelTypeParam.BULK_ADD:
        return (prevSidePanelWidth.current = '60%')
      case SidePanelTypeParam.RECAP:
        return (prevSidePanelWidth.current = '60%')
      case SidePanelTypeParam.AGENT_TASK:
        return (prevSidePanelWidth.current = 'min(90%, 580px)')
      default:
        return prevSidePanelWidth.current
    }
  }, [contentType])

  const preventShortcutBubbling = useCallback((e: React.KeyboardEvent) => {
    const shortcut = shortcutFromEvent(e)
    // Prevent all known shortcuts from bubbling through to the table underneath
    // Don't prevent escape or meta+enter (form submit) from bubbling to the Primer handler
    if (
      shortcut !== SHORTCUTS.ESCAPE &&
      shortcut !== SHORTCUTS.META_ENTER &&
      Object.values(SHORTCUTS).includes(shortcut)
    )
      e.stopPropagation()
  }, [])

  return (
    <SidePanelComponent
      open={open}
      onClose={onEscape}
      returnFocusRef={returnFocusRef}
      topOffset={siteHeaderHeight}
      onKeyDown={preventShortcutBubbling}
      width={sidePanelWidth}
      aria-label={label}
      initialFocusRef={initialFocusRef}
      defaultCloseElement="first-child"
    >
      {children}
    </SidePanelComponent>
  )
})

/**
 * Create a side panel label string for accessible region labelling. The result should be consistent in format so the
 * region is easy to find, but should also reflect the contents of the panel.
 */
function buildSidePanelLabel(state: SidePanelState) {
  const parts = [Resources.sidePanelRegionNameLabel]

  switch (state?.type) {
    case SidePanelTypeParam.INFO:
      parts.push(Resources.sidePanelProjectInfoLabel)
      break
    case SidePanelTypeParam.BULK_ADD:
      parts.push(Resources.sidePanelBulkAddLabel)
      break
    case SidePanelTypeParam.RECAP:
      parts.push(Resources.sidePanelRecapLabel)
      break
    case SidePanelTypeParam.AGENT_TASK:
      parts.push(Resources.sidePanelAgentTaskLabel)
      break
    case SidePanelTypeParam.ISSUE:
      parts.push(Resources.itemType(state.item.contentType), state.item.getRawTitle() ?? '')
  }

  return parts.join(': ')
}
